import { createHeadlessEditor } from '@lexical/headless';
import { $createParagraphNode, $createTextNode, $getRoot, type SerializedEditorState } from 'lexical';
import { RICH_TEXT_NODES } from './lexical-config';

/**
 * Server-side utility to convert plain text to Lexical EditorState.
 *
 * Based on official Lexical documentation and GitHub discussions:
 * - https://lexical.dev/docs/concepts/editor-state
 * - https://github.com/facebook/lexical/discussions/3850
 *
 * This function takes plain text and converts it to proper Lexical JSON format,
 * handling line breaks by creating separate paragraph nodes.
 *
 * ⚠️ SERVER-SIDE ONLY - Do not import this in client-side code
 *
 * @param plainText - Plain text string (can contain \n line breaks)
 * @returns Promise<SerializedEditorState | null> - Lexical JSON state or null if no content
 */
export const plainTextToLexical = async (plainText: string | null): Promise<SerializedEditorState | null> => {
    if (!plainText?.trim()) {
        return null;
    }

    try {
        // Create headless editor with rich text nodes
        const editor = createHeadlessEditor({
            namespace: 'PlainTextConverter',
            nodes: RICH_TEXT_NODES,
            onError: (error) => {
                console.error('Plain text to Lexical conversion error:', error);
            },
        });

        // Convert plain text to Lexical using Promise-based approach
        return new Promise<SerializedEditorState | null>((resolve, reject) => {
            try {
                // Register update listener to capture the final state
                const removeListener = editor.registerUpdateListener(({ editorState }) => {
                    // Clean up listener immediately
                    removeListener();
                    // Resolve with the JSON representation
                    resolve(editorState.toJSON());
                });

                // Perform the update with discrete: true for immediate execution
                editor.update(
                    () => {
                        const root = $getRoot();

                        // Clear any existing content
                        root.clear();

                        // Split text by line breaks to create separate paragraphs
                        const lines = plainText.split(/\r?\n/);

                        // Filter out empty lines at start/end, but preserve internal empty lines
                        const trimmedLines = lines.reduce((acc, line, index, arr) => {
                            // Always include non-empty lines
                            if (line.trim()) {
                                acc.push(line);
                            }
                            // Include empty lines if they're between non-empty lines
                            else if (index > 0 && index < arr.length - 1) {
                                // Check if there are non-empty lines after this empty line
                                const hasContentAfter = arr.slice(index + 1).some((l) => l.trim());
                                if (hasContentAfter) {
                                    acc.push(''); // Preserve internal empty line
                                }
                            }
                            return acc;
                        }, [] as string[]);

                        // If no meaningful content, create empty paragraph
                        if (trimmedLines.length === 0) {
                            const emptyParagraph = $createParagraphNode();
                            root.append(emptyParagraph);
                            return;
                        }

                        // Create paragraph nodes for each line
                        trimmedLines.forEach((line) => {
                            const paragraph = $createParagraphNode();

                            if (line.trim()) {
                                // Non-empty line: create text node
                                const textNode = $createTextNode(line);
                                paragraph.append(textNode);
                            }
                            // Empty line: paragraph remains empty (creates visual line break)

                            root.append(paragraph);
                        });
                    },
                    { discrete: true },
                ); // Force immediate execution
            } catch (error) {
                reject(error);
            }
        });
    } catch (error) {
        console.error('Error creating headless editor for plain text conversion:', error);
        return null;
    }
};

/**
 * Simplified synchronous version for basic use cases.
 * Creates a minimal Lexical state structure directly without using the editor.
 *
 * This is faster but less robust than the headless editor approach.
 * Use plainTextToLexical() for production code.
 */
export const plainTextToLexicalSync = (plainText: string | null): SerializedEditorState | null => {
    if (!plainText?.trim()) {
        return null;
    }

    try {
        // Split by line breaks and filter empty lines at start/end
        const lines = plainText.split(/\r?\n/).filter((line, index, arr) => {
            if (line.trim()) return true;
            // Keep empty lines that are between non-empty lines
            const hasContentBefore = arr.slice(0, index).some((l) => l.trim());
            const hasContentAfter = arr.slice(index + 1).some((l) => l.trim());
            return hasContentBefore && hasContentAfter;
        });

        if (lines.length === 0) {
            return null;
        }

        // Create Lexical JSON structure manually
        const children = lines.map((line) => ({
            children: line.trim()
                ? [
                      {
                          detail: 0,
                          format: 0,
                          mode: 'normal' as const,
                          style: '',
                          text: line,
                          type: 'text' as const,
                          version: 1,
                      },
                  ]
                : [],
            direction: 'ltr' as const,
            format: '',
            indent: 0,
            type: 'paragraph' as const,
            version: 1,
        }));

        return {
            root: {
                children,
                direction: 'ltr' as const,
                format: '',
                indent: 0,
                type: 'root' as const,
                version: 1,
            },
        };
    } catch (error) {
        console.error('Error in synchronous plain text to Lexical conversion:', error);
        return null;
    }
};
